import React, { useEffect, useState } from 'react';

import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import makeStyles from '@material-ui/core/styles/makeStyles';
import { useRewardsPendingCount } from 'contexts/rewardsPendingCount';

import Pending from './components/pending/Pending';
import Approved from './components/approved/Approved';
import Declined from './components/declined/Declined';
import SearchInput from 'components/list/SearchInput';

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
  },
  container: {},
  searchRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  searchInput: {
    width: '100%',
    maxWidth: 320,
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  tabCount: {
    minWidth: theme.spacing(4),
    padding: '4px',
    borderRadius: theme.spacing(2),
    backgroundColor: 'rgba(99, 84, 245, 0.12)',
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.2,
    textAlign: 'center',
  },
}));

enum TabValues {
  Pending = 'Pending',
  Approved = 'Approved',
  Declined = 'Declined',
}

const RewardsSettingsList = (props) => {
  const classes = useStyles();
  const [tab, setTab] = useState<TabValues>(TabValues.Pending);
  const { pendingCount, pendingCountLoading, pendingCountError, refreshPendingCount } =
    useRewardsPendingCount();
  const [searchValues, setSearchValues] = useState<Record<TabValues, string>>({
    [TabValues.Pending]: '',
    [TabValues.Approved]: '',
    [TabValues.Declined]: '',
  });
  const [debouncedSearchValues, setDebouncedSearchValues] = useState<Record<TabValues, string>>({
    [TabValues.Pending]: '',
    [TabValues.Approved]: '',
    [TabValues.Declined]: '',
  });

  const handleChangeTab = (event, value) => {
    setTab(value);
  };

  // Assumes the pending badge should refresh when the Pending tab becomes active.
  useEffect(() => {
    if (tab === TabValues.Pending) {
      refreshPendingCount();
    }
  }, [tab, refreshPendingCount]);

  const handleSearchChange = (value) => {
    setSearchValues((prev) => ({
      ...prev,
      [tab]: value,
    }));
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchValues(searchValues);
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchValues]);

  const currentSearchValue = searchValues[tab];
  const currentAppliedSearchValue = debouncedSearchValues[tab];

  const renderTabLabel = (value: TabValues, label: string) => {
    if (value !== TabValues.Pending) {
      return label;
    }

    const showBadge = pendingCount !== null && !pendingCountError;

    return (
      <Box className={classes.tabLabel}>
        {label}
        {showBadge ? <Box className={classes.tabCount}>{pendingCount}</Box> : null}
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>Rewards Verification</Typography>
      <Tabs
        component={React.Fragment}
        indicatorColor="primary"
        textColor="primary"
        value={tab}
        onChange={handleChangeTab}>
        <Tab value={TabValues.Pending} label={renderTabLabel(TabValues.Pending, 'Pending')} />
        <Tab value={TabValues.Approved} label="Approved" />
        <Tab value={TabValues.Declined} label="Declined" />
      </Tabs>
      <Box className={classes.searchRow}>
        <Box className={classes.searchInput}>
          <SearchInput
            label="Search"
            onlySearch
            fullWidth
            loading={pendingCountLoading && tab === TabValues.Pending}
            input={{
              value: currentSearchValue,
              onChange: handleSearchChange,
              name: 'search',
            }}
          />
        </Box>
      </Box>
      {tab === TabValues.Pending && (
        <Pending
          {...props}
          search={currentAppliedSearchValue}
          status="PENDING"
          onStatusChange={refreshPendingCount}
        />
      )}
      {tab === TabValues.Approved && (
        <Approved {...props} search={currentAppliedSearchValue} status="APPROVED" />
      )}
      {tab === TabValues.Declined && (
        <Declined {...props} search={currentAppliedSearchValue} status="REJECTED" />
      )}
    </Box>
  );
};

export default RewardsSettingsList;
